/**
 * Market state — ONE reader for "which market conditions are live right now".
 *
 * Every presentation of a mutation (the floor overlay, the fog, the tremor,
 * the grade, the banner's condition chips) used to reach for the predictor's
 * chaos ledger on its own, or not at all: the three ambient kinds and the
 * body-side mutators had no client presentation whatsoever, which is why a
 * "MARKET EVENT: FOG" announced a fog that never came. They all read this
 * module now, so a condition that is live for the sim is live for every cue
 * on the same tick.
 *
 * TWO LEDGERS, deliberately. Mechanical mutations live in the sim's own
 * `world.chaos.applied`. Ambient ones (fog, colour shift, tremor) NEVER do:
 * `applyMutation` returns before touching any state for them, so a wagered
 * match's checksum cannot depend on weather (mutations.ts, property 4). The
 * client learns about them from the room's `chaos_event` apply message and
 * keeps its own window here, on the same tick clock, expiring by the same
 * `msToTicks` the sim would use. Bound to the world it was recorded against,
 * so a new match starts clean.
 *
 * Nothing here is a rule. The gravity, speed, scale and inversion readouts
 * come from `@chaos/sim`'s own queries (the same functions `stepWorld` calls),
 * so the cue can never disagree with the physics; the ambient readouts are
 * the payloads themselves, because the sim has no reader for them.
 */

import { TICK_HZ } from '@chaos/protocol';
import { MUTATOR_SPECS } from '@chaos/director';
import {
  chaosGravityMultiplier,
  chaosSpeedMultiplier,
  chaosControlsInverted,
  chaosPlayerScale,
  isAmbientKind,
  isMutation,
  msToTicks,
} from '@chaos/sim';

/**
 * Where the client learns which mutators are live.
 *
 * In production this is the predictor's world: `chaos_event` and the snapshot
 * `chaos` ledger carry mutation payloads, the predictor folds them into
 * `world.chaos.applied`, and its constructor publishes the world through
 * `__chaosPredictedWorld`. A global handle rather than an import, matching
 * the gym/debug probes, because the predictor must stay free of the
 * three/webgpu stack the renderer drags in. No source means no overlay rather
 * than a crash. The arena mockup tool (`interventions.js`) swaps in a
 * synthetic world through `setChaosWorldSource`.
 */
let _source = () =>
  globalThis.__chaosPredictedWorld?.() ??
  globalThis.gym?.game?.predictor?.world ??
  globalThis.chaos?.game?.predictor?.world ??
  null;

export function setChaosWorldSource(fn) {
  _source = fn;
}

export function chaosWorld() {
  return _source?.() ?? null;
}

/** The sim's own window rule (`body.ts` isMutationActive): [tick, untilTick). */
export function isLive(entry, tick) {
  return tick >= entry.tick && tick < entry.untilTick;
}

// ─── The ambient ledger ─────────────────────────────────────

const _ambient = { world: null, entries: [] };

/**
 * Records an ambient event the room said has LANDED. Announce-phase events,
 * mechanical kinds (the sim records those itself) and malformed payloads are
 * ignored. `ev.tick` is the room's apply tick; without one, the world's own.
 */
export function recordChaosEvent(ev, world = chaosWorld()) {
  if (ev?.phase !== 'apply' || !isMutation(ev.mutation) || !isAmbientKind(ev.mutation.kind)) return null;
  if (_ambient.world !== world) {
    _ambient.world = world;
    _ambient.entries.length = 0;
  }
  const tick = Number.isInteger(ev.tick) ? ev.tick : (world?.tick ?? 0);
  const entry = {
    kind: ev.mutation.kind,
    tick,
    untilTick: tick + msToTicks(ev.mutation.durationMs ?? 0),
    mutation: ev.mutation,
  };
  _ambient.entries.push(entry);
  // Expired entries are dropped here rather than kept forever: unlike the
  // sim's ledger this one is not a checksum, and a long match would otherwise
  // hold every shower of weather it ever had.
  if (world) {
    for (let i = _ambient.entries.length - 1; i >= 0; i--) {
      if (_ambient.entries[i].untilTick <= world.tick) _ambient.entries.splice(i, 1);
    }
  }
  return entry;
}

/** The gym's "Clear mutators", and a match ending. */
export function clearAmbientLedger() {
  _ambient.world = null;
  _ambient.entries.length = 0;
}

// ─── Match end ──────────────────────────────────────────────

/**
 * The world whose presentation has ENDED. At match end (and on a leave) the
 * snapshots stop and the predictor's tick freezes, so every entry in its
 * ledger stays "live" for as long as the results screen is up — a tremor
 * rumbled and a fog hung over the results. Every reader here answers
 * "nothing" for that world; the ledger itself is untouched (it is the sim's
 * audit trail, never the presentation's to edit). Bound to the world object:
 * the next match's predictor is a new one and is live from its first tick.
 */
let _endedWorld = null;

export function endMarket(world = chaosWorld()) {
  _endedWorld = world ?? null;
}

export function isMarketEnded(world) {
  return world != null && world === _endedWorld;
}

/** Both ledgers, every entry (live or not), for a world. `out` is reused. */
export function allEntries(world, out = []) {
  out.length = 0;
  if (!world || isMarketEnded(world)) return out;
  const applied = world.chaos?.applied;
  if (applied) for (const e of applied) out.push(e);
  if (_ambient.world === world) for (const e of _ambient.entries) out.push(e);
  return out;
}

const _scratch = [];

/**
 * The live conditions, ONE per kind, in the order they were first applied.
 *
 * Two overlapping rolls of the same kind are one condition to a player (the
 * sim takes the strongest; the chip shows the longest), and the gym applies
 * a mutation to both the sim world and the predictor, so a raw ledger can
 * carry the same event twice. `out` is reused by per-frame callers.
 */
export function activeConditions(world, out = []) {
  out.length = 0;
  if (!world) return out;
  const tick = world.tick;
  for (const e of allEntries(world, _scratch)) {
    if (!isLive(e, tick)) continue;
    let seen = null;
    for (const c of out) if (c.kind === e.kind) { seen = c; break; }
    if (seen) {
      if (e.untilTick > seen.untilTick) {
        seen.untilTick = e.untilTick;
        seen.mutation = e.mutation;
      }
      continue;
    }
    out.push({ kind: e.kind, mutation: e.mutation, tick: e.tick, untilTick: e.untilTick });
  }
  return out;
}

/** Milliseconds a condition has left, on the sim's clock. */
export function remainingMs(condition, world) {
  return Math.max(0, ((condition.untilTick - world.tick) / TICK_HZ) * 1000);
}

/** Every number the cues need, in one object. `out` is reused by callers. */
export function marketReadout(world, out = {}) {
  out.fog = 0;
  out.tremor = 0;
  out.hue = null;
  out.gravity = 1;
  out.speed = 1;
  out.scale = 1;
  out.inverted = false;
  if (!world?.chaos?.applied || isMarketEnded(world)) return out;
  out.gravity = chaosGravityMultiplier(world);
  out.speed = chaosSpeedMultiplier(world);
  out.scale = chaosPlayerScale(world);
  out.inverted = chaosControlsInverted(world);
  // Ambient kinds come from the client ledger in production; a synthetic
  // world (the arena tool, a test) may carry them in `applied` too.
  const tick = world.tick;
  for (const e of allEntries(world, _scratch)) {
    if (!isLive(e, tick)) continue;
    const m = e.mutation;
    if (m.kind === 'fog_roll') out.fog = Math.max(out.fog, m.densityPct / 100);
    else if (m.kind === 'tremor') out.tremor = Math.max(out.tremor, m.magnitudePct / 100);
    // The most recent shift owns the grade — one look at a time, like the floor.
    else if (m.kind === 'color_shift') out.hue = m.hueDeg;
  }
  return out;
}

/** The director's own copy. Never re-authored here. */
export function labelFor(kind) {
  return MUTATOR_SPECS[kind]?.label ?? String(kind).replace(/_/g, ' ').toUpperCase();
}

export function describeKind(kind) {
  return MUTATOR_SPECS[kind]?.description ?? '';
}
